/**
 * Os cartões de link estão ligados para quem está olhando? (27/10/2026)
 *
 * Interno: vem do interruptor da empresa (`GET /chat/configuracoes`). `null` enquanto carrega
 * (a tela ainda não pede cartão nem monta a barra); se a leitura falhar devolve `true`, porque
 * quem decide de verdade é o `ligada` da própria consulta — um GET fora do ar não pode apagar
 * os cartões.
 *
 * Portal: o parceiro NÃO tem o GET do interruptor. O estado vem do `ligada` das respostas da
 * consulta, que a consulta grava em `CHAVE_LIGADA_DO_PORTAL` a cada resposta; sem nada gravado
 * vale `true` (padrão ligado).
 *
 * Os dois `useQuery` rodam SEMPRE (regra dos hooks), nas duas origens; só um conta. O do chat
 * fica desligado no portal e o do portal nunca busca nada.
 */
import { skipToken, useQuery } from "@tanstack/react-query";

import type { OrigemDasPrevias } from "@/services/previasDeLinkApi";

import { useConfiguracoesDoChat } from "./useConfiguracoesDoChat";

/** Onde a consulta do portal grava o `ligada` da última resposta. */
export const CHAVE_LIGADA_DO_PORTAL = ["previas-de-link", "ligada", "portal"] as const;

export function useCartoesLigados(origem: OrigemDasPrevias): boolean | null {
  const configuracao = useConfiguracoesDoChat({ enabled: origem === "interno" });

  // Só um lugar para guardar o booleano, sem busca: `queryFn: skipToken` (o "sem função de busca"
  // do TanStack), `enabled: false`, `initialData: true` e `staleTime: Infinity`. Dois jeitos errados:
  // omitir o `queryFn` faz o `useQuery` dar `console.error` a CADA render fora de produção (medido:
  // 2 em 2 renders; sujaria o console do E2E de dev); e um `queryFn: async () => true` real faz o
  // `false` gravado voltar a `true` quando a query vence. O `skipToken` não busca nunca, então o
  // valor gravado pela consulta só muda por `setQueryData`.
  const doPortal = useQuery<boolean>({
    queryKey: CHAVE_LIGADA_DO_PORTAL,
    queryFn: skipToken,
    enabled: false,
    initialData: true,
    staleTime: Infinity,
  });

  if (origem === "portal") return doPortal.data;
  if (configuracao.data) return configuracao.data.previa_de_link_ligada;
  if (configuracao.isError) return true;
  return null;
}
